<script setup>
import { usePageHead } from '@composables/use-page-head'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useAppearanceStore } from '@state/stores/appearance'
import { useMemberStore } from '@state/stores/member'
import { usePremiumStore } from '@state/stores/premium'
import { useSettingsStore } from '@state/stores/settings'
import { useMessages } from '@composables/use-messages'
import { onDemandQualityOptions } from '../../../configuration'
import settingsService from '../../../services/settings-service'
import DataLayer from '@services/data-layer/index'
import modalService from '../../../services/modal-service'

const appearanceStore = useAppearanceStore()
const memberStore = useMemberStore()
const premiumStore = usePremiumStore()
const settingsStore = useSettingsStore()
const getMessageText = useMessages()

const options = onDemandQualityOptions
const selectedOption = ref('')

const memberId = computed(() => memberStore.currentMember.id)

const currentOnDemandQualityKey = computed(() => settingsStore.currentOnDemandQualityKey)

const isPremium = computed(() => premiumStore.isPremium)

const hasEverBeenPremium = computed(() => premiumStore.hasEverBeenPremium)

async function save(qualityKey) {
    const savedQuality = await settingsService.setCurrentOnDemandQuality(qualityKey)
    if (!savedQuality) return

    settingsStore.setSettings({ currentOnDemandQualityKey: qualityKey })
    selectedOption.value = currentOnDemandQualityKey.value

    appearanceStore.handleNotification({
        message: getMessageText('AUDIO_QUALITY_UPDATED'),
        type: 'success',
    })
}

function handleQualitySelection(event, qualityKey) {
    if (!qualityKey) return

    if (options[qualityKey].needsSub && !isPremium.value) {
        event.preventDefault()
        event.stopPropagation()

        // The radio has already taken the click, so put it back on the current tier.
        nextTick(() => {
            selectedOption.value = currentOnDemandQualityKey.value
        })

        const isEntitledFreeTrial = !hasEverBeenPremium.value
        modalService.showPremiumUpsell({
            headline: getMessageText('PREMIUM_UPSELL_HEADLINE'),
            description: null,
            subDescription: isEntitledFreeTrial
                ? getMessageText('PREMIUM_UPSELL_SUBHEADLINE')
                : null,
            entitledFreeTrial: isEntitledFreeTrial,
            primaryAction: {
                text: isEntitledFreeTrial
                    ? getMessageText('PREMIUM_UPSELL_CTA_FREE_TRIAL')
                    : getMessageText('PREMIUM_UPSELL_CTA_UPGRADE'),
                route: '/premium/view-plans',
            },
            secondaryAction: {
                text: getMessageText('PREMIUM_UPSELL_SECONDARY'),
                route: '/premium/view-plans',
            },
        })
        return
    }

    save(qualityKey)
}

async function getSubscriptions() {
    const subscriptions = await DataLayer.subscriptions.getSubscriptionsByMemberId(memberId.value)
    premiumStore.setSubscriptions(subscriptions.body)
    selectedOption.value = currentOnDemandQualityKey.value
}

watch(currentOnDemandQualityKey, newValue => {
    selectedOption.value = newValue
}, { immediate: true })

onMounted(getSubscriptions)

usePageHead({ title: 'Settings - Audio Quality', description: 'Settings - Audio Quality' })
</script>

<template>
    <BaseSection padded>
        <h6 class="subtitle">Audio Quality</h6>

        <div class="quality-selection">
            <div
                v-for="(value, name) in options"
                :key="'audioQuality' + name"
                class="quality"
            >
                <input
                    :id="'audioQuality' + name"
                    v-model="selectedOption"
                    class="radio"
                    :value="name"
                    type="radio"
                    name="audioQuality"
                    @click="handleQualitySelection($event, name)"
                >
                <div>
                    <label
                        class="label"
                        :for="'audioQuality' + name"
                    >{{
                        value.title
                    }}</label>
                    <label
                        class="sublabel"
                        :for="'audioQuality' + name"
                    >{{
                        value.subtitle
                    }}</label>
                </div>
            </div>
        </div>
        <div class="note">
            <p>Audio quality affects radio and on-demand streaming. Changing audio quality takes effect from the next playback.</p>
        </div>
    </BaseSection>
</template>

<style lang="scss" scoped>
@import '@design';

// %radio fills an unchecked control with $c-surface-raised — the same surface this panel
// uses, so the control disappeared into it. Black against the panel instead. Overridden here
// rather than in _elements.scss because the reason is local: it is this panel the control has
// to be seen against.
.radio {
    @extend %radio;

    margin-right: $s-s;
    cursor: pointer;
    background: $c-black;
}

// Bold: the tier is the choice being made, and the line under it only explains it.
.label {
    @extend %contentSubtitleM;

    font-weight: 700;
    cursor: pointer;
}

.sublabel {
    @extend %contentSubtitleS;

    display: block;
    cursor: pointer;
}

.subtitle {
    @extend %contentSubtitleS;

    min-width: 100%;
    margin: 0;
    text-align: center;
}

// The three options are one panel, not three loose rows (Figma 859:57) — and the same
// 16px card radius the External Players tab gives its channel rows, so the two Settings
// tabs agree. The padding is what keeps the `.quality ~ .quality` rule below reading as a
// divider BETWEEN rows rather than a border ON the panel: the border is drawn on the
// sibling row, so the panel's inset holds it clear of the rounded edge.
.quality-selection {
    padding: $s-l;
    background: $c-surface-raised;
    border-radius: $size-radius-m;
}

.quality {
    display: flex;
    align-items: top;

    .label,
    .sublabel {
        color: $c-text-primary;
    }

    ~ .quality {
        padding-top: $s-xl;
        margin-top: $s-xl;
        border-top: 1px solid transparentize($c-text-tertiary, 0.9);
    }

    &:hover {
        .label,
        .sublabel {
            color: $c-white;
        }
        .radio {
            background: transparentize($c-highlight, 0.5);
        }
    }
}

.note {
    @extend %contentSubtitleM;

    margin-top: $s-xxxl;

    p {
        margin: 0;
    }
}

@include tablet {
    .subtitle {
        text-align: left;
    }
}
</style>
